import {
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useState
} from 'react';
import { useLingui } from '@lingui/react/macro';
import { useSettings } from '@wu/widget-core';
import { shuffle } from 'es-toolkit';
import {
  getColumns,
  getFormattedManualImages,
  getLayoutObjFromName
} from '../../helpers';
import { useImages } from '../images';
import { useLightbox } from '../lightbox';

export const LoadMore = ({ children, tablet, mobile, images }) => {
  const settings = useSettings();
  const {
    layout,
    gridRowsPerPage,
    verticalMasonryImagesPerPage,
    horizontalMasonryImagesPerPage,
    panoramicRowsPerPage,
    gridLoadMoreButton,
    verticalMasonryLoadMoreButton,
    horizontalMasonryLoadMoreButton,
    panoramicLoadMoreButton,
    imageOrder,
    demoImages
  } = settings;
  const { images: allImages, isLoading } = useImages();

  const { t } = useLingui();

  const filteredImages = useMemo(() => {
    if (isLoading) {
      return [];
    }

    const finalImages = allImages?.length
      ? images
      : getFormattedManualImages(demoImages);

    return finalImages?.filter(({ isEmpty }) => !isEmpty);
  }, [allImages?.length, images, demoImages, isLoading]);

  const [items, setItems] = useState(filteredImages);
  const [shuffledItems, setShuffledItems] = useState([]);

  const { name } = getLayoutObjFromName(layout);
  const { setSources } = useLightbox();

  const [cursor, setCursor] = useState(0);

  const {
    default: columnsDefault,
    tablet: columnsTablet,
    mobile: columnsMobile
  } = getColumns(settings);

  const columns = useMemo(() => {
    if (tablet) {
      return columnsTablet;
    }

    if (mobile) {
      return columnsMobile;
    }

    return columnsDefault;
  }, [tablet, mobile, columnsTablet, columnsMobile, columnsDefault]);

  const needLoadMore = useMemo(() => {
    return (
      {
        grid: gridLoadMoreButton,
        verticalMasonry: verticalMasonryLoadMoreButton,
        horizontalMasonry: horizontalMasonryLoadMoreButton,
        panoramic: panoramicLoadMoreButton
      }[name] || false
    );
  }, [
    name,
    gridLoadMoreButton,
    verticalMasonryLoadMoreButton,
    horizontalMasonryLoadMoreButton,
    panoramicLoadMoreButton
  ]);

  const perPage = useMemo(() => {
    const perLayoutPage = {
      grid: columns * gridRowsPerPage,
      verticalMasonry: columns * verticalMasonryImagesPerPage,
      horizontalMasonry: horizontalMasonryImagesPerPage,
      panoramic: panoramicRowsPerPage
    }[name];

    if (needLoadMore && perLayoutPage) {
      return perLayoutPage;
    }

    return items.length;
  }, [
    name,
    needLoadMore,
    columns,
    gridRowsPerPage,
    items,
    verticalMasonryImagesPerPage,
    horizontalMasonryImagesPerPage,
    panoramicRowsPerPage
  ]);

  const [end, setEnd] = useState(false);

  const getNewStackValue = useCallback(() => {
    return items.slice(0, cursor + perPage);
  }, [items, cursor, perPage]);

  const [stack, setStack] = useState(getNewStackValue());

  const loadMoreSettings = [
    gridRowsPerPage,
    verticalMasonryImagesPerPage,
    horizontalMasonryImagesPerPage,
    panoramicRowsPerPage,
    gridLoadMoreButton,
    verticalMasonryLoadMoreButton,
    horizontalMasonryLoadMoreButton,
    panoramicLoadMoreButton,
    imageOrder
  ];

  useEffect(() => {
    setCursor(0);
    setStack(getNewStackValue());
  }, [name, filteredImages.length, ...loadMoreSettings]);

  useEffect(() => {
    if (isLoading) {
      return;
    }

    if (imageOrder === 'random') {
      const finalImages = allImages?.length
        ? allImages
        : getFormattedManualImages(demoImages);
      if (shuffledItems.length === finalImages.length) {
        const finalItems = shuffledItems.map((item) =>
          finalImages.find((elem) => elem.id === item.id)
        );
        setShuffledItems(finalItems);
      } else {
        setShuffledItems(shuffle(finalImages));
      }
    } else {
      setShuffledItems([]);
    }
    // shuffledItems.length не нужен, иначе есть шанс цикличного вызова хука
    // oxlint-disable-next-line react-hooks/exhaustive-deps
  }, [allImages, imageOrder, demoImages, isLoading]);

  useEffect(() => {
    if (isLoading) {
      return;
    }

    if (imageOrder === 'random') {
      const finalItems = shuffledItems.filter((item) =>
        filteredImages.find((elem) => elem.id === item.id)
      );

      setItems(finalItems);
    } else {
      setItems(filteredImages);
    }
    // imageOrder убран специально, т.к. он уже является триггером для
    // предыдущего хука с setShuffledItems, а shuffledItems должен триггерить
    // этот хук, иначе с imageOrder появляется моргание при переключении order
    // oxlint-disable-next-line react-hooks/exhaustive-deps
  }, [filteredImages, shuffledItems, isLoading]);

  useEffect(() => setSources(items), [items, imageOrder, setSources]);

  useLayoutEffect(() => {
    const newStack = getNewStackValue();

    setStack(newStack);
    setEnd(newStack.length === items.length);
  }, [getNewStackValue]);

  const loadMore = () => setCursor((prevCursor) => prevCursor + perPage);

  return children({
    images: needLoadMore ? stack : items,
    loadMore,
    end,
    showButton: needLoadMore,
    buttonText: t`Load More`
  });
};
